import { endpoints } from '@/config/api';
import { apiClient } from '@/lib/apiClient';
import type {
  CreateFiscalYearDTO,
  FiscalMonth,
  FiscalYear,
  UpdateFiscalMonthDTO,
  UpdateFiscalYearDTO,
  UpsertSettingsDTO,
  UserSettings,
} from '@/types';

/**
 * Settings Service - user settings, fiscal years and fiscal months
 */
export const settingsService = {
  // ── User Settings ────────────────────────────────────────────────────────────

  async getSettings(): Promise<UserSettings | null> {
    return apiClient.get<UserSettings | null>(endpoints.settings.get);
  },

  async upsertSettings(settings: UpsertSettingsDTO): Promise<UserSettings> {
    return apiClient.put<UserSettings>(endpoints.settings.update, settings);
  },

  // ── Fiscal Years ─────────────────────────────────────────────────────────────

  async getFiscalYears(): Promise<FiscalYear[]> {
    return apiClient.get<FiscalYear[]>(endpoints.fiscalYears.list);
  },

  async createFiscalYear(data: CreateFiscalYearDTO): Promise<FiscalYear> {
    return apiClient.post<FiscalYear>(endpoints.fiscalYears.create, data);
  },

  async updateFiscalYear(id: string, data: UpdateFiscalYearDTO): Promise<FiscalYear> {
    return apiClient.put<FiscalYear>(endpoints.fiscalYears.update(id), data);
  },

  async deleteFiscalYear(id: string): Promise<void> {
    return apiClient.delete(endpoints.fiscalYears.delete(id));
  },

  // ── Fiscal Months ─────────────────────────────────────────────────────────────

  async getFiscalMonths(fiscalYearId: string): Promise<FiscalMonth[]> {
    return apiClient.get<FiscalMonth[]>(endpoints.fiscalYears.months(fiscalYearId));
  },

  async updateFiscalMonth(id: string, data: UpdateFiscalMonthDTO): Promise<FiscalMonth> {
    return apiClient.put<FiscalMonth>(endpoints.fiscalMonths.update(id), data);
  },
};
